import { ActionCreators } from 'redux-undo';
import type {
    AspectRatio,
    AudioSourceData,
    IconSourceData,
    MediaSourceData,
    OpaqueVideoData,
    Scene,
    TemplateColor,
    TextAsset,
} from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { UserOverrides } from '@lumen5/luminary/virtual-designs/core';
import type { SerializedEditorState, SerializedLexicalNode } from 'lexical';

import type { ScriptSegment } from '../script-composer';

import type {
    BrandKit,
    BulkPublishResponseItem,
    BulkTextSegmentationRequest,
    BulkTextSegmentationResponse,
    DashboardVideo,
    LanguageEnum,
    LanguageLocalesEnum,
    TranscriptWord,
    TranslateVideoV2Response,
    Video,
    VideoUpdateResponse,
} from 'js/src/client';
import {
    ApiService,
    CreationSourceEnum,
    DashboardFlowEnum,
    ErrorEnum,
    SegmentationTypeEnum,
    StatusEnum,
    TaskResultMessageTypeEnum,
    Urls,
} from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { initializeTabDurations, syncDurationForTab } from 'js/src/data/analytics';
import type { EDITOR_TAB_OPTIONS, VIDEO_INIT_TYPE, VideoApprovalStatusData } from 'js/src/data/app';
import {
    getCurrentEditorTab,
    markSceneGroupAsFinishedLoading,
    markSceneGroupAsLoading,
    SCENE_GROUP_LOADING_TYPES,
    setAsyncGenerationData,
    setAsyncGenerationTaskId,
    setBlueprintId,
    setIsAnimationVideo,
    setIsEditingDraft,
    setLoadingCreatorActivityId,
    setParentVideoLanguage,
    setSelectedTemplateId,
    setSharePageUrl,
    setStage,
    setVideoApprovalStatus,
    setVideoAssignee,
    setVideoCreationSource,
    setVideoDataHash,
    setVideoId,
    setVideoInitType,
    setVideoLanguage,
    setVideoOwnerId,
    setVideoProject,
    setVideoSlug,
    setVideoVisibility,
    STAGES,
    switchEditorTabAction,
    UNKNOWN_SAVE_ERROR_MESSAGE,
} from 'js/src/data/app';
import {
    getAsyncGenerationData,
    getAsyncGenerationTaskId,
    getCreateVideoFlowAnalyticsMethod,
    getCreateVideoFlowAnalyticsThemeId,
    getDashboardFlowFromQueryStringOrRedux,
    getProjectId,
    getSelectedAspectRatio,
    getSelectedBlueprintId,
    getSelectedBrandKitId,
    getSelectedPaletteIndex,
    getSelectedTemplateId,
    getSharePageUrl,
    getVideoCreationSource,
    getVideoDataHash,
    getVideoId,
    getVideoInitType,
    isTextOnMediaGenerating,
} from 'js/src/data/app/app-selectors';
import {
    getMultiVideoByVideoId,
    getMultiVideoIds,
    setVideoName,
} from 'js/src/data/app/multi-videos-slice';
import { showMediaErrorOperation } from 'js/src/data/app/show-media-error-operation';
import {
    storeArticleId,
    storeArticleLanguageCode,
    storeArticleUrl,
} from 'js/src/data/article/article-slice';
import { isPlaceholderArticleUrl } from 'js/src/data/article/article-utils';
import { getBlueprint } from 'js/src/data/blueprints/blueprints-selectors';
import { getBrandKitById, loadBrandKits } from 'js/src/data/brand-kits';
import {
    storeDocumentId,
    storeDocumentLanguageCode,
    storeDocumentName,
    storeDocumentUrl,
} from 'js/src/data/document';
import { ensureFontsLoaded, getFontList } from 'js/src/data/fonts';
import { setCurrentAudioPlayingStatus, setMediaErrors } from 'js/src/data/media';
import { showModal } from 'js/src/data/modals/modals-actions';
import { LOCKED_FEATURES, MODAL_IDS } from 'js/src/data/modals/modals-types';
import {
    setSceneSelectorForSlide,
    setSwapSceneDesignForSlide,
    showFullscreenSelector,
    showFullscreenSwapSceneSelector,
} from 'js/src/data/scenes';
import type { ScriptComposerData } from 'js/src/data/script-composer';
import {
    getAIScriptData,
    getScriptComposerData,
    getScriptComposerStartPageFromFlow,
    hydrateOriginalContent,
    hydrateScriptComposerData,
} from 'js/src/data/script-composer';
import {
    loadTemplateAndSceneDesigns,
    loadTemplatesAndCategories,
} from 'js/src/data/templates/templates-operations';
import {
    getTemplateBundle,
    getTemplateColorPalettes,
    getTemplateDefaultStoryboardSceneDesign,
} from 'js/src/data/templates/templates-selectors';
import { luminaryPromiseChain } from 'js/src/data/thumbnail-previews/thumbnail-previews-operations';
import {
    applyTranslatedCaptionTranscriptV2Operation,
    buildTranslatedTranscript,
    hydrateTranscripts,
} from 'js/src/data/transcript/transcript-operations';
import type { SerializedTranscript } from 'js/src/data/transcript/transcript-types';
import { getFullTranscriptFromEditor } from 'js/src/data/transcript/transcript-utils';
import {
    getEditorFromSceneGroupId,
    getTranscriptsToSave,
} from 'js/src/data/transcript/transcript-selectors';
import { storeTranslatedEditor } from 'js/src/data/translated-editors';
import { getSupportedLanguages } from 'js/src/data/translations';
import { convertLocaleToLanguageCode } from 'js/src/data/translations/utils';
import { getAllSessionUploadedAssetUuids } from 'js/src/data/uploads/uploads-selectors';
import { loadUsageLimits } from 'js/src/data/usage-limit/usage-limit-operations';
import { hydrateOverrides } from 'js/src/data/user-overrides';
import { getUserOverrides } from 'js/src/data/user-overrides/user-overrides-selectors';
import { setBrandKitId } from 'js/src/data/video';
import { getTabDurationsFromVideo } from 'js/src/data/video/utils';
import { getBrandKitId } from 'js/src/data/video/video-selectors';
import { setVideoData } from 'js/src/data/video-data/video-data-actions';
import { userCanPerformUndoAction } from 'js/src/data/video-data/video-data-selectors';
import { handleVideoLimitError } from 'js/src/hooks/video-limit-error';
import { AUTO_DOWNLOAD_PARAM, PLAN_PREFIX, toMutableCopy } from 'js/src/libs/consts';
import { waitForMillis } from 'js/src/libs/custom-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Navigation from 'js/src/libs/navigation';
import QueryStringManipulator from 'js/src/libs/querystring-manipulator';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';
import { parseScript } from 'js/src/libs/transcript';
import uuid from 'js/src/libs/uuid';
import WebSocketClient from 'js/src/libs/websocket-client';
import { updateVideoTextDirectionOperation } from 'js/src/luminary';
import { applyBrandKitToLuminary } from 'js/src/luminary/brand-kit-operations';
import { withDataLuminarySync } from 'js/src/luminary/data-luminary';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    getNarrativeThreads,
    isAIVoiceoverSceneGroup,
    isOrdinarySceneGroup,
    isOutroSceneGroup,
    isVoiceDrivenSceneGroup,
} from 'js/src/luminary/luminary-selectors';
import { saveRawLuminary } from 'js/src/luminary/utils';
import { isApiError, isApiErrorWithProperty } from 'js/src/types';
import { pruneForSave } from 'js/src/utils/prune-orphaned-video-data';

export function switchEditorTab(nextTab: EDITOR_TAB_OPTIONS) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        dispatch(syncDurationForTab({ currentTab: getCurrentEditorTab(getState()) }));
        dispatch(switchEditorTabAction({ nextTab }));
    };
}

export function clearUndoableHistory() {
    return (dispatch: AppDispatch) => {
        dispatch(ActionCreators.clearHistory());
    };
}

export function updateVideoName(name: string, videoId: number) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        ApiService.videoPartialUpdate({
            path: { video_id: videoId.toString() },
            body: { name },
        })
            .then((response) => {
                dispatch(setVideoNameOperation(response.data.name));
                dispatch(
                    setVideoDataHash({
                        videoDataHash: response.data.video_data_hash,
                    }),
                );
            })
            .catch((error: unknown) => {
                Toast.error(
                    'Something went wrong with renaming your video, please try again later.',
                );
                ErrorReporter.capture(error);
            });
    };
}

/**
 * Returns video data for a basic video created from a template.
 * The video contents are a single scene video (using the default storyboard scene design).
 */
export function createVideoDataFromTemplate(
    templateId: string,
    aspectRatio: AspectRatio,
    paletteIndex?: number | null,
    brandKitId?: number | null,
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        // get template details from redux
        const templateBundle = getTemplateBundle(getState(), templateId);
        const defaultStoryboardSceneDesign = getTemplateDefaultStoryboardSceneDesign(
            getState(),
            templateId,
        );
        if (!defaultStoryboardSceneDesign)
            throw new Error('No default storyboard scene design found');

        // create video data for video using template (1 scene – default storyboard scene!)
        return withDataLuminarySync(
            (dataLuminary) => {
                dataLuminary.aspectRatio = aspectRatio;

                // Apply brand kit or selected palette
                if (brandKitId !== null && brandKitId !== undefined) {
                    const brandKit = getBrandKitById(getState(), brandKitId);
                    if (brandKit) {
                        const fontList = getFontList(getState());
                        applyBrandKitToLuminary(
                            dataLuminary,
                            toMutableCopy<BrandKit>(brandKit),
                            fontList,
                        );
                    } else {
                        ErrorReporter.capture(
                            `Brand kit ${brandKitId} not found in Redux during video creation`,
                        );
                    }
                } else if (paletteIndex !== null && paletteIndex !== undefined) {
                    const colorPalettes = getTemplateColorPalettes(getState(), templateId);
                    const palette = colorPalettes?.[paletteIndex];
                    if (palette) {
                        const colors = toMutableCopy<TemplateColor[]>(palette.colors);
                        const existingColors = dataLuminary.template.colors;
                        if (colors.length < existingColors.length) {
                            // a video could be made when the template had N colors,
                            // then the template was changed to have N-1 colors.
                            // Safest is to just add the existing trailing colors to the new palette.
                            colors.push(...existingColors.slice(colors.length));
                        }
                        dataLuminary.template.colors = colors;
                    }
                }

                dataLuminary.addScene({
                    sceneDesignId: defaultStoryboardSceneDesign,
                });
                return saveRawLuminary(dataLuminary);
            },
            { templateBundle: templateBundle ?? undefined },
        );
    };
}

export const createVideoOperation = createLuminaryOperation(
    async ({ dispatch, getState, call }, { enterCreatorStage }: { enterCreatorStage: boolean }) => {
        const selectedTemplateId = getSelectedTemplateId(getState());
        if (!selectedTemplateId) {
            throw new Error(`invalid selectedTemplateId: ${selectedTemplateId}`);
        }

        const selectedBlueprint = getBlueprint(getState(), getSelectedBlueprintId(getState()));
        const selectedAspectRatio = getSelectedAspectRatio(getState());
        const projectId = getProjectId(getState());
        if (selectedBlueprint) {
            await dispatch(loadTemplateAndSceneDesigns(selectedTemplateId));
            return call(createVideoFromBlueprintOperation, {
                projectId,
                aspectRatio: selectedAspectRatio,
            });
        } else {
            try {
                const dashboardFlow = getDashboardFlowFromQueryStringOrRedux(getState());
                const selectedPaletteIndex = getSelectedPaletteIndex(getState());
                const selectedBrandKitId = getSelectedBrandKitId(getState());
                const videoId = await dispatch(
                    createVideoFromTemplateOperation({
                        templateId: selectedTemplateId,
                        aspectRatio: selectedAspectRatio,
                        paletteIndex: selectedPaletteIndex,
                        brandKitId: selectedBrandKitId,
                        projectId: projectId,
                        dashboardFlow: dashboardFlow,
                    }),
                );
                await call(onCreateVideoSuccessOperation, {
                    videoId,
                    aspectRatio: selectedAspectRatio,
                    enterCreatorStage,
                });
                if (selectedBrandKitId !== null) {
                    dispatch(
                        setBrandKitId({
                            brandKitId: selectedBrandKitId,
                            systemTriggered: true,
                            parentActionGroup: undefined,
                        }),
                    );
                }
            } catch (err) {
                // Backstop for a stale client-side limit check: the backend rejects video
                // creation once the free-plan cap is hit. Surface the upgrade modal instead of a
                // generic failure.
                if (!handleVideoLimitError(err, dispatch)) {
                    ErrorReporter.capture(err);
                }
                dispatch(onCreateVideoFail());
            }
        }
    },
);

/**
 * Create a video from a template with the specified parameters
 * This is extracted logic from the create_video function in index.tsx
 * Returns the created video info including videoId and share page URL
 */
export const createVideoFromTemplateOperation = ({
    templateId,
    aspectRatio,
    paletteIndex,
    brandKitId,
    projectId,
    dashboardFlow,
}: {
    templateId: string;
    aspectRatio: AspectRatio;
    paletteIndex?: number | null;
    brandKitId?: number | null;
    projectId: number | null;
    dashboardFlow: DashboardFlowEnum | null;
}) => {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        // Load template and scene designs
        await dispatch(loadTemplateAndSceneDesigns(templateId));

        // Brand kit data and fonts may not be in Redux yet — ensure both are loaded before
        // createVideoDataFromTemplate tries to look up the brand kit by ID. Only load the
        // brand kits if the requested one isn't already available.
        if (brandKitId != null) {
            if (getBrandKitById(getState(), brandKitId) == null) {
                await dispatch(loadBrandKits());
            }
            await dispatch(ensureFontsLoaded());
        }

        // Create video data from template
        const videoData = dispatch(
            createVideoDataFromTemplate(templateId, aspectRatio, paletteIndex, brandKitId),
        );

        // Create the video via API
        const response = await ApiService.videoCreate({
            body: {
                video_data: videoData,
                aspect_ratio: String(aspectRatio),
                template: templateId,
                script_composer_data: {
                    page: dispatch(getScriptComposerStartPageFromFlow(dashboardFlow)),
                    selected_ai_voice: null,
                    import_type: null,
                    dashboard_flow: dashboardFlow,
                },
                project_id: projectId ?? undefined,
                creation_source:
                    dashboardFlow === DashboardFlowEnum.SCREEN_RECORDING
                        ? CreationSourceEnum.SCREEN_RECORDINGS
                        : CreationSourceEnum.SCRIPT_COMPOSER,
            },
        });

        return response.data.video_id;
    };
};

export const createVideoFromBlueprintOperation = createLuminaryOperation(
    async (
        { dispatch, getState, call },
        {
            projectId,
            aspectRatio,
        }: {
            projectId: number | null;
            aspectRatio: AspectRatio;
        },
    ) => {
        try {
            const selectedBlueprint = getBlueprint(getState(), getSelectedBlueprintId(getState()));
            const selectedTemplateId = getSelectedTemplateId(getState());
            const blueprintId = selectedBlueprint?.id;
            if (!blueprintId) throw new Error('selected_blueprint_id is null');
            if (!selectedTemplateId) throw new Error('selected_template_id is null');

            const dashboardFlow = getDashboardFlowFromQueryStringOrRedux(getState());

            const isStartFromScratchFlow = dashboardFlow === DashboardFlowEnum.START_FROM_SCRATCH;

            let video: DashboardVideo;
            if (isStartFromScratchFlow) {
                const response = await ApiService.videosDuplicateVideosCreate({
                    body: {
                        video_ids: [selectedBlueprint.video_ids[0]],
                        with_prefix: true,
                        use_default_name: true,
                    },
                });
                video = response.data[0];
            } else {
                const newVideoResponse = await ApiService.videosCreateFromBlueprintCreate({
                    body: {
                        blueprint_id: blueprintId,
                        target_project_id: projectId ?? undefined,
                    },
                });
                video = newVideoResponse.data;
            }
            const videoId = video.id;

            const scriptComposerData = {
                script_composer_data: {
                    page: dispatch(getScriptComposerStartPageFromFlow(dashboardFlow)),
                    selected_ai_voice: null,
                    import_type: null,
                    dashboard_flow: dashboardFlow,
                },
            };

            const updateResponse = await ApiService.videoPartialUpdate({
                path: { video_id: videoId.toString() },
                body: {
                    template_id: selectedTemplateId,
                    ...(isStartFromScratchFlow ? {} : scriptComposerData),
                },
            });

            dispatch(
                setVideoDataHash({
                    videoDataHash: updateResponse.data.video_data_hash,
                }),
            );

            analytics.track('New video created from blueprint', {
                videoId,
                blueprintId,
            });
            const enterCreatorStage = true;
            call(onCreateVideoSuccessOperation, { videoId, aspectRatio, enterCreatorStage });
        } catch (err) {
            ErrorReporter.capture(err);
            dispatch(onCreateVideoFail());
        }
    },
);

export const onCreateVideoSuccessOperation = createLuminaryOperation(
    async (
        { dispatch, getState, call },
        {
            videoId,
            aspectRatio,
            enterCreatorStage,
        }: {
            videoId: number;
            aspectRatio: AspectRatio;
            enterCreatorStage: boolean;
        },
    ) => {
        let newUrl = window.location.href;
        newUrl = QueryStringManipulator.updateQueryStringParameter(
            newUrl,
            'id',
            videoId.toString(),
        );

        const flow = getDashboardFlowFromQueryStringOrRedux(getState());
        if (flow) {
            newUrl = QueryStringManipulator.removeQueryStringParameter(newUrl, 'flow');
        }
        window.history.replaceState({ path: newUrl }, '', newUrl);

        const targetPage = dispatch(getScriptComposerStartPageFromFlow(flow));
        const skipSetStage = targetPage !== null;
        const isEditingDraft = false;
        await call(moveToLoadingEditVideoStageOperation, {
            videoId,
            isEditingDraft,
            skipSetStage,
            enterCreatorStage,
        });

        analytics.track('Theme stage 2.0 completed', {
            method: getCreateVideoFlowAnalyticsMethod(getState()),
            themeId: getCreateVideoFlowAnalyticsThemeId(getState()),
            aspectRatio: aspectRatio,
            paletteIndex: getSelectedPaletteIndex(getState()),
            brandKitId: getSelectedBrandKitId(getState()),
        });
        if (flow) {
            analytics.track('Dashboard video creation flow completed', {
                flow,
                isUsingBlueprints: !!getBlueprint(getState(), getSelectedBlueprintId(getState())),
            });
        }
    },
);

export const onCreateVideoFail = () => {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        analytics.track('Error Creating New Video', {
            template: getSelectedTemplateId(getState()),
        });
        dispatch(
            showModal({
                modal_id: MODAL_IDS.GENERIC_ERROR,
                modal_context: {
                    title: 'Oops!',
                    text: 'An unexpected error occurred while creating your video. Please try again.',
                },
            }),
        );
        window.history.replaceState({}, '', window.location.pathname);
        dispatch(setBlueprintId({ blueprintId: null }));
        dispatch(setStage({ stage: STAGES.NEW_CREATE_VIDEO_FLOW_PAGE }));
    };
};

export const moveToLoadingEditVideoStageOperation = createLuminaryOperation(
    async (
        { dispatch, getState, call },
        {
            videoId,
            isEditingDraft,
            skipSetStage,
            enterCreatorStage,
        }: {
            videoId: number;
            isEditingDraft: boolean;
            skipSetStage: boolean;
            enterCreatorStage: boolean;
        },
    ) => {
        const newActivityId = uuid();
        ActivityProfiler.start(newActivityId);
        if (!skipSetStage) {
            dispatch(setStage({ stage: STAGES.LOADING_EDIT_VIDEO }));
        }
        dispatch(setLoadingCreatorActivityId({ loadingCreatorActivityId: newActivityId }));
        dispatch(setIsEditingDraft({ isEditingDraft }));

        try {
            const response = await ApiService.videoRetrieve({
                path: { video_id: videoId.toString() },
                query: {
                    require_editable_version: true,
                },
            });

            const video = response.data;
            const scriptComposerPage = (video.script_composer_data as ScriptComposerData | null)
                ?.page;
            if (isEditingDraft && scriptComposerPage) {
                analytics.track('Incomplete Video Opened', {
                    videoId,
                    script_composer_page: scriptComposerPage,
                    video_init_type: video.video_init_type,
                });
            }

            // Start fetching the template bundle as soon as we know the template id
            if (video.template_id) {
                // Errors are already reported and surfaced to the user by the operation itself;
                // swallow the rejection here so running it unawaited doesn't go unhandled.
                void dispatch(loadTemplateAndSceneDesigns(video.template_id)).catch(() => {});
            }

            const { video_data } = dispatch(hydrateVideoCreatorReduxStoresAction(video));

            await call(moveToCreatorStageOperation, video_data, enterCreatorStage);
        } catch (error) {
            if (
                isApiError(error) &&
                error.status === 409 &&
                isApiErrorWithProperty(error, 'share_page_url')
            ) {
                analytics.track('Media Step: Rendered Video Attempted to be Edited!', {
                    video_id: videoId,
                });
                const share_page_url = error.body.share_page_url as string;
                window.setTimeout(() => {
                    updateShouldShowLeaveWarning(false);
                    window.location.href = share_page_url;
                }, 50);
                return;
            }
            if (
                isApiError(error) &&
                error.status === 406 &&
                isApiErrorWithProperty(error, 'workspace_id')
            ) {
                const workspaceId = error.body.workspace_id as number;
                await ApiService.changeWorkspaceUpdate({
                    path: { workspace_id: workspaceId.toString() },
                });
                location.reload();
                return;
            }
            analytics.track('Error Loading Video to Edit', { id: videoId });
            showModal({
                modal_id: MODAL_IDS.GENERIC_ERROR,
                modal_context: {
                    text: 'There was an error loading that video. Please try again.',
                    title: 'Ooops!',
                },
            });
            dispatch(loadTemplatesAndCategories());
            dispatch(
                setStage({
                    stage: STAGES.NEW_CREATE_VIDEO_FLOW_PAGE,
                }),
            );
        }
    },
);

export const moveToCreatorStageOperation = createLuminaryOperation(
    async (
        { dispatch, getState, luminary },
        videoData: { [key: string]: unknown },
        enterCreatorStage: boolean,
    ) => {
        await luminaryPromiseChain.cancelAndWaitForCurrentTask();
        await luminary.clear();

        const systemTriggered = false;
        const parentActionGroup: string | undefined = undefined;
        dispatch(
            setVideoData({
                video_data: videoData,
                systemTriggered,
                parentActionGroup,
            }),
        );

        dispatch(clearUndoableHistory());

        if (enterCreatorStage) {
            dispatch(setStage({ stage: STAGES.CREATOR }));
        }

        if (getVideoCreationSource(getState()) === CreationSourceEnum.TRANSLATION) {
            const sceneGroupIds = luminary.sceneGroups.map((sg) => sg.id);
            for (const sceneGroupId of sceneGroupIds) {
                dispatch(
                    markSceneGroupAsLoading({
                        sceneGroupId,
                        type: SCENE_GROUP_LOADING_TYPES.SIMPLE_MESSAGE,
                        context: { loadingMsg: 'Loading translated video...' },
                    }),
                );
            }

            luminary.onAllSlideImagesRendered().then(() => {
                for (const sceneGroupId of sceneGroupIds) {
                    dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));
                }
            });
        }
    },
);

export function fetchVideoApprovalStatus(videoId: number) {
    return (dispatch: AppDispatch) => {
        return new Promise<VideoApprovalStatusData>((resolve, reject) => {
            return ApiService.videoApprovalStatusRetrieve({
                path: { video_id: videoId },
            })
                .then((response) => {
                    const { status = null, assignee = null } = response.data;
                    dispatch(setVideoApprovalStatus({ status }));
                    dispatch(setVideoAssignee({ assignee }));
                    resolve({ status, assignee });
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    reject(error);
                });
        });
    };
}

let isCurrentlySaving = false;

type SaveSpecificVideoOperationParams = {
    videoId: number;
    videoData: OpaqueVideoData | null;
    isForceSave?: boolean;
};
export const saveSpecificVideoOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        { videoId, videoData, isForceSave }: SaveSpecificVideoOperationParams,
    ) => {
        // The system doesn't like multiple simultaneous save requests because it messes up with our "video data hash"
        // system for ensuring we're not overwriting each other's changes. So we use the `isCurrentlySaving` as a lock
        // to prevent multiple saves from happening at the same time.
        if (isCurrentlySaving) {
            // wait until the current save is done
            await new Promise<void>((resolve) => {
                const interval = setInterval(() => {
                    if (!isCurrentlySaving) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 100);
            });
        }
        isCurrentlySaving = true;

        // collect the request data
        let video_data: OpaqueVideoData;
        try {
            video_data = videoData || saveRawLuminary(luminary);
        } catch (error) {
            // Release the lock, or every later save waits on it forever.
            isCurrentlySaving = false;
            throw error;
        }

        const videoInitType = getVideoInitType(getState());
        const { userOverrides, transcripts: videoTranscripts } = pruneForSave(
            {
                userOverrides: getUserOverrides(getState()),
                transcripts: getTranscriptsToSave(getState()),
            },
            luminary,
        );
        const allSessionUploadedAssetUuids = [...getAllSessionUploadedAssetUuids(getState())];
        const tabDurations = getState().analytics.tab_durations;
        const templateId = luminary.template.id;
        const aspectRatio = luminary.aspectRatio;
        const brandKitId = getBrandKitId(getState());
        const blueprintId = getSelectedBlueprintId(getState());
        const videoUpdatedByUser = !!userCanPerformUndoAction(getState());
        const videoDataHash = getVideoDataHash(getState());

        if (videoDataHash === null) {
            // Release the lock first: this throw is outside the try/finally that resets it.
            isCurrentlySaving = false;
            throw new Error('No video data hash found');
        }
        const { editedEditorState } = getAIScriptData(getState());
        const articleOriginalContent = editedEditorState
            ? (editedEditorState.toJSON() as unknown as {
                  [key: string]: unknown;
              })
            : null;

        const scriptComposerData = getScriptComposerData(getState());
        const script = parseScript(luminary);

        const narrative_threads = getNarrativeThreads(luminary);
        // Preserve async generation state while transmog is actively running,
        // so that the transcript sidebar stays blocked (inert + BlurBackdrop).
        const isTransmogRunning = isTextOnMediaGenerating(getState());
        const async_generation_id = isTransmogRunning ? getAsyncGenerationTaskId(getState()) : null;
        const async_generation_data = isTransmogRunning ? getAsyncGenerationData(getState()) : null;

        let response_data: VideoUpdateResponse | null = null;
        try {
            const response = await ApiService.videoUpdate({
                path: { video_id: videoId.toString() },
                body: {
                    video_data: video_data,
                    script,
                    tab_durations: tabDurations,
                    video_data_hash: videoDataHash,
                    template_id: templateId,
                    aspect_ratio: aspectRatio,
                    render: false,
                    user_overrides: userOverrides,
                    user_uploads: allSessionUploadedAssetUuids,
                    brand_kit_id: brandKitId,
                    blueprint_id: blueprintId,
                    transcripts: videoTranscripts,
                    video_init_type: videoInitType,
                    script_composer_data: scriptComposerData,
                    article_original_content: articleOriginalContent,
                    video_updated_by_user: videoUpdatedByUser,
                    narrative_threads,
                    async_generation_id,
                    async_generation_data,
                    is_force_save: isForceSave,
                    duration_seconds: Math.floor(luminary.duration / 1000),
                    scene_count: luminary.scenes.length,
                },
            });
            response_data = response.data;
        } finally {
            isCurrentlySaving = false;
        }
        // When saving to a different video, don't update the hash.
        // Else, that would prevent saving the current video again.
        const currentVideoId = getVideoId(getState());
        if (currentVideoId === videoId) {
            dispatch(
                setVideoDataHash({
                    videoDataHash: response_data.videoDataHash,
                }),
            );
            // Update the video language/text position
            if (response_data.language) {
                dispatch(
                    setVideoLanguage({
                        video_language: response_data.language,
                    }),
                );
                call(updateVideoTextDirectionOperation);
            }
            // Clear async generation state from Redux after successful save,
            // but only if transmog is not actively running
            if (!isTextOnMediaGenerating(getState())) {
                dispatch(setAsyncGenerationTaskId({ taskId: null }));
                dispatch(setAsyncGenerationData(null));
            }
        }
        return response_data;
    },
);

export const sendVideoPublishAnalyticsEventsOperation = createLuminaryOperation(
    ({ luminary, getState }) => {
        return new Promise<void>((resolve) => {
            const state = getState();
            analytics.track(
                'Video Submitted to Render',
                {
                    aspect_ratio: luminary.aspectRatio,
                    video_id: getVideoId(state),
                },
                () => {
                    resolve();
                },
            );
        });
    },
);

export function setVideoIdAndHookupAnalytics(videoId: number | string) {
    return (dispatch: AppDispatch) => {
        if (typeof videoId === 'string') {
            videoId = parseFloat(videoId);
        }
        // set the analytics video_id so we can track events connected to this video
        if (analytics && analytics.setVideoIdContext) {
            analytics.setVideoIdContext(videoId);
        }
        dispatch(setVideoId({ videoId }));
    };
}

export function setVideoNameOperation(name: string) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        // update page title
        document.title = `${name} | Lumen5`;
        dispatch(setVideoName({ videoName: name, videoId: getVideoId(getState()) }));
    };
}

type TSubmitGPTFeedbackParams = {
    feedback: string;
    emoji: string;
    videoId: number;
    blueprintId?: number;
    source: string;
    page?: string;
    flow?: string;
};
export function submitGPTFeedback({
    feedback,
    emoji,
    videoId,
    blueprintId,
    source,
    page,
    flow,
}: TSubmitGPTFeedbackParams) {
    Toast.info('Thank you for your feedback!');
    return ApiService.shareGptFeedbackCreate({
        body: {
            text: feedback,
            emoji,
            video_id: videoId,
            blueprint_id: blueprintId,
            source,
            page,
            flow,
        },
    });
}

/**
 * Gets word counts for each text in a list of texts. This is useful when you need more precise word counts
 * (e.g per sentence) but don't want to send multiple requests to getWordCount
 */
export function getBatchWordCount(blocks: Array<string>, language: string | undefined) {
    const request = { batches: [] } as BulkTextSegmentationRequest;
    blocks.forEach((text) => {
        request.batches.push({
            text,
            language_code: language,
            segmentation_type: SegmentationTypeEnum.WORD,
        });
    });
    return new Promise<Array<ScriptSegment>>((resolve, reject) => {
        ApiService.bulkTextSegmentationCreate({ body: request })
            .then((response) => {
                resolve(
                    response.data.batches.map((batch) => {
                        return {
                            text: batch.text,
                            wordCount: batch.words.length,
                        };
                    }),
                );
            })
            .catch((error: unknown) => {
                reject(error);
                ErrorReporter.capture(error);
            });
    });
}

export function segmentText(
    blocks: Array<string>,
    language: string | undefined,
): Promise<BulkTextSegmentationResponse['batches']> {
    const request = { batches: [] } as BulkTextSegmentationRequest;
    blocks.forEach((text) => {
        request.batches.push({
            text,
            language_code: language,
            segmentation_type: SegmentationTypeEnum.WORD,
        });
    });
    return new Promise((resolve, reject) => {
        ApiService.bulkTextSegmentationCreate({ body: request })
            .then((response) => {
                resolve(response.data.batches);
            })
            .catch((error: unknown) => {
                reject(error);
                ErrorReporter.capture(error);
            });
    });
}

export function getWordCount(text: string, language?: string): Promise<number> {
    // if the text is just whitespace or empty, return 0
    if (!text.trim()) {
        return Promise.resolve(0);
    }
    return getBatchWordCount([text], language).then((results) => {
        return results[0].wordCount;
    });
}

export const translateVideoV2Operation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        videoId: number,
        language: LanguageLocalesEnum,
        langSupportAIVoiceover: boolean,
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        const taskCreatorResponse = await ApiService.videoTranslateV2Create({
            path: { video_id: videoId.toString() },
            body: {
                target_language: language,
            },
        });
        const ws = WebSocketClient.getInstance();
        const translatedVideo = await ws.waitForMessage<TranslateVideoV2Response>(
            taskCreatorResponse.data.task_id,
            TaskResultMessageTypeEnum.TASK_RESULT,
            'translate_video_v2',
        );

        // Apply text asset translations
        luminary.batchRun(() => {
            for (const sg of translatedVideo.scene_groups) {
                for (const scene of sg.scenes) {
                    const luminaryScene = luminary.getSceneById(scene.id);
                    if (!luminaryScene) continue;
                    for (const ta of scene.text_assets) {
                        const segments = ta.text.split(' || ');
                        if (segments.length > 1 && luminaryScene.subscenes.length > 0) {
                            // Multiple segments from || grouping — distribute across subscenes
                            luminaryScene.subscenes.forEach((subscene, i) => {
                                if (i >= segments.length) return;
                                const subsceneAsset = subscene.getAssetByName(ta.name) as
                                    | TextAsset
                                    | undefined;
                                if (subsceneAsset) {
                                    subsceneAsset.text = segments[i].trim();
                                    subsceneAsset.textHighlights = [];
                                    subsceneAsset.setFontSizeToFitAllText();
                                }
                            });
                        } else {
                            const textAsset = luminaryScene.getAssetByName(ta.name, {
                                deep: true,
                            }) as TextAsset | undefined;
                            if (textAsset) {
                                textAsset.text = ta.text;
                                textAsset.textHighlights = ta.text_highlights ?? [];
                                textAsset.setFontSizeToFitAllText();
                            }
                        }
                    }
                }
            }
        });

        dispatch(
            setVideoCreationSource({
                creationSource: CreationSourceEnum.TRANSLATION,
            }),
        );

        // Apply caption translations for voice-driven scene groups
        const supportedLanguages = getSupportedLanguages(getState());
        const parentLanguage = convertLocaleToLanguageCode(supportedLanguages, language);
        if (!parentLanguage) {
            throw new Error('Target language is not supported');
        }

        const captionPromises: Promise<void>[] = [];
        for (const translatedSg of translatedVideo.scene_groups) {
            if (translatedSg.type === 'text') continue;

            const sceneGroup = luminary.getSceneGroupById(translatedSg.id);
            if (!sceneGroup || !isVoiceDrivenSceneGroup(sceneGroup)) {
                // eslint-disable-next-line no-console
                console.warn(
                    `[translateVideoV2] Skipping scene group ${translatedSg.id} (type=${translatedSg.type}): not found in luminary or not voice-driven`,
                );
                continue;
            }
            if (!langSupportAIVoiceover && isAIVoiceoverSceneGroup(sceneGroup)) {
                // eslint-disable-next-line no-console
                console.warn(
                    `[translateVideoV2] Skipping AI voiceover scene group ${translatedSg.id}: language does not support AI voiceover`,
                );
                continue;
            }

            // Collect per-scene caption text from the LLM response
            const segments = translatedSg.scenes.map((s) => s.captions ?? '');
            const captionText = segments.filter((c) => c.trim().length > 0).join(' ');
            if (!captionText.trim()) {
                // eslint-disable-next-line no-console
                console.warn(
                    `[translateVideoV2] Skipping scene group ${translatedSg.id}: LLM returned empty captions`,
                );
                continue;
            }

            const doTranslate = async () => {
                // Build transcript words — for talking head, the backend
                // returns aligned_transcript_words with proper timings from
                // TranscriptAligner; for AI voiceover, use placeholder
                // timings (ElevenLabs provides real timings later)
                let transcriptWords: TranscriptWord[];
                if (translatedSg.aligned_transcript_words?.length) {
                    // Timings are already in millis (from Luminary data)
                    transcriptWords = translatedSg.aligned_transcript_words.map((w) => ({
                        word: w.word,
                        startTime: w.startTime,
                        endTime: w.endTime,
                        filler: w.filler ?? false,
                    }));
                } else {
                    const originalEditor = getEditorFromSceneGroupId(getState(), sceneGroup.id);
                    if (!originalEditor) {
                        // eslint-disable-next-line no-console
                        console.warn(
                            `[translateVideoV2] Skipping scene group ${translatedSg.id}: no aligned_transcript_words and no original editor in state`,
                        );
                        return;
                    }
                    const originalWords = getFullTranscriptFromEditor(originalEditor);
                    if (originalWords.length === 0) {
                        // eslint-disable-next-line no-console
                        console.warn(
                            `[translateVideoV2] Skipping scene group ${translatedSg.id}: no aligned_transcript_words and original editor has no words`,
                        );
                        return;
                    }

                    const translatedWords = captionText.split(/\s+/).filter((w) => w.length > 0);
                    const totalOrigDuration =
                        originalWords[originalWords.length - 1].endTime -
                        originalWords[0].startTime;
                    const startTime = originalWords[0].startTime;
                    const durationPerWord =
                        translatedWords.length > 0 ? totalOrigDuration / translatedWords.length : 0;

                    transcriptWords = translatedWords.map((word, i) => ({
                        word,
                        startTime: startTime + i * durationPerWord,
                        endTime: startTime + (i + 1) * durationPerWord,
                        filler: false,
                    }));
                }

                const translatedTranscriptEditor = await dispatch(
                    buildTranslatedTranscript(transcriptWords, parentLanguage),
                );
                dispatch(
                    storeTranslatedEditor({
                        sceneGroupId: sceneGroup.id,
                        editor: translatedTranscriptEditor,
                        langCode: parentLanguage,
                        lexicalControlsItsOwnState: false,
                    }),
                );
                await call(
                    applyTranslatedCaptionTranscriptV2Operation,
                    sceneGroup.id,
                    parentLanguage,
                    segments,
                    parentActionGroup,
                    systemTriggered,
                );
            };
            captionPromises.push(doTranslate());
        }

        await Promise.all(captionPromises);
    },
);

export const setAsyncGeneration = (
    asyncGenerationTaskId: string,
    asyncGenerationData: unknown = null,
) => {
    return (dispatch: AppDispatch) => {
        if (asyncGenerationData) {
            const bootstrapData = asyncGenerationData as {
                sceneGroups: {
                    id: string;
                    avatarAsset: {
                        id: string;
                        volume: number;
                        sourceData: MediaSourceData;
                    } | null;
                    voiceoverAsset: {
                        id: string;
                        volume: number;
                        sourceData: AudioSourceData;
                        playbackRegions: [number, number][];
                    } | null;
                    mediaAsset: {
                        id: string;
                        volume: number;
                        sourceData: MediaSourceData;
                        playbackRegions: [number, number][];
                    } | null;
                    originalTranscript: { words: TranscriptWord[] };
                }[];
            };
            const allSceneGroupData = bootstrapData.sceneGroups
                .filter(
                    (sceneGroup) =>
                        sceneGroup.voiceoverAsset !== null || sceneGroup.mediaAsset !== null,
                )
                .map((sceneGroup) => {
                    const transcriptWords = sceneGroup.originalTranscript.words;
                    let avatarSourceData = null;
                    let mediaSourceData = null;
                    let voiceoverSourceData = null;
                    let volume = 100;
                    let playbackRegions: [number, number][] = [];
                    if (sceneGroup.voiceoverAsset) {
                        voiceoverSourceData = sceneGroup.voiceoverAsset.sourceData;
                        volume = sceneGroup.voiceoverAsset.volume;
                        playbackRegions = sceneGroup.voiceoverAsset.playbackRegions;
                        if (sceneGroup.avatarAsset) {
                            avatarSourceData = sceneGroup.avatarAsset.sourceData;
                        }
                    } else if (sceneGroup.mediaAsset) {
                        mediaSourceData = sceneGroup.mediaAsset.sourceData;
                        volume = sceneGroup.mediaAsset.volume;
                        playbackRegions = sceneGroup.mediaAsset.playbackRegions;
                    }
                    return {
                        voiceoverSourceData,
                        mediaSourceData,
                        avatarSourceData,
                        volume,
                        playbackRegions,
                        transcriptWords,
                    };
                });
            dispatch(
                setAsyncGenerationData({
                    sceneGroups: allSceneGroupData,
                }),
            );
        }
        dispatch(setAsyncGenerationTaskId({ taskId: asyncGenerationTaskId }));
    };
};

export const publishAllVideosOperation = createLuminaryOperation(
    async (
        { luminary, call, dispatch, getState },
        { autoDownload }: { autoDownload?: string } = {},
    ) => {
        // do a couple book-keeping operations before publishing
        dispatch(setCurrentAudioPlayingStatus({ isPlaying: false }));
        dispatch(syncDurationForTab({ currentTab: getCurrentEditorTab(getState()) }));

        // Remove blank stickers (media asset stickers with empty sourceData).
        // These can occur when a user adds a sticker but doesn't select one,
        // leaving an empty placeholder that blocks video publishing.
        let removedStickerCount = 0;
        luminary.scenes.forEach((scene) => {
            scene
                .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
                .filter(
                    (asset) =>
                        asset.name.startsWith('sticker') &&
                        (!asset.sourceData || Object.keys(asset.sourceData).length === 0),
                )
                .forEach((sticker) => {
                    sticker.destroy();
                    removedStickerCount++;
                });
        });
        if (removedStickerCount > 0) {
            analytics.track('Removed invalid stickers', { count: removedStickerCount });
        }

        const mainVideoId = getVideoId(getState());
        let bulkPublishResult: BulkPublishResponseItem[] = [];
        try {
            // save the main video first so the server has the latest version
            if (typeof mainVideoId === 'number') {
                await call(saveSpecificVideoOperation, {
                    videoId: mainVideoId,
                    videoData: null,
                });
            }

            // publish all the videos
            bulkPublishResult = await call(bulkPublishMultiVideosOperation);
        } catch (error) {
            if (typeof mainVideoId !== 'number') {
                throw new Error('No main video ID found');
            }
            bulkPublishResult.push({
                video_id: mainVideoId,
                status: StatusEnum.ERROR,
                error_code: ErrorEnum.UNKNOWN_STATE,
                details: null,
            });
        }

        // Handle the results after all videos have been processed
        const successfulResults = bulkPublishResult.filter(
            (result) => result.status === StatusEnum.SUCCESS,
        );
        const failedResults = bulkPublishResult.filter(
            (result) => result.status === StatusEnum.ERROR,
        );

        if (failedResults.length > 0) {
            // If any video failed, take the action for the first failure
            failedResults.forEach((failedResult) => {
                analytics.track('Publish Button Clicked But Unable To Publish', {
                    valid: false,
                    reason: failedResult.error_code,
                    video_id: failedResult.video_id,
                });
            });
            const firstFailure = failedResults[0];
            call(handlePublishFailureOperation, firstFailure);
            return;
        }
        await call(handlePublishSuccessOperation, { successfulResults, autoDownload });
    },
);

const bulkPublishMultiVideosOperation = createLuminaryOperation(async ({ getState }) => {
    const multiVideoIds = getMultiVideoIds(getState());
    const mainVideoId = getVideoId(getState());
    const videoIdsToPublish: number[] = [];
    if (mainVideoId) {
        videoIdsToPublish.push(mainVideoId);
    }
    if (multiVideoIds.length > 0) {
        videoIdsToPublish.push(...multiVideoIds.filter((id) => id !== mainVideoId));
    }

    const response = await ApiService.videosBulkPublishCreate({
        body: {
            video_ids: videoIdsToPublish,
        },
    });
    return response.data.results;
});

const handlePublishSuccessOperation = createLuminaryOperation(
    async (
        { call, getState, dispatch },
        {
            successfulResults: successfulPublishResponses,
            autoDownload,
        }: { successfulResults: BulkPublishResponseItem[]; autoDownload?: string },
    ) => {
        // TODO: currently sendVidePublishAnalyticsEventOperation only works for the main video.
        // If there are multiVideos open in the creator, we should extend it to work for all of the videos
        // being published.
        await call(sendVideoPublishAnalyticsEventsOperation);
        if (successfulPublishResponses.length === 1) {
            // if the creator only has one video open, navigate to the share page
            const result = successfulPublishResponses[0];
            const mainVideoId = getVideoId(getState());
            const sharePageUrl =
                mainVideoId === result.video_id
                    ? getSharePageUrl(getState())
                    : getMultiVideoByVideoId(getState(), result.video_id)?.page_url;
            if (!sharePageUrl) {
                const error = new Error('Share page URL is undefined after publishing');
                ErrorReporter.capture(error, {
                    extra: {
                        publishedVideoId: result.video_id,
                        mainVideoId,
                        isMainVideo: mainVideoId === result.video_id,
                    },
                });
                throw error;
            }
            updateShouldShowLeaveWarning(false);
            // Carries a queued download (e.g. carousel images) to the share page, which fires it
            // once the render finishes.
            Navigation.goToLocation(
                autoDownload
                    ? `${sharePageUrl}?${AUTO_DOWNLOAD_PARAM}=${autoDownload}`
                    : sharePageUrl,
            );
        } else if (successfulPublishResponses.length > 1) {
            // If multiple videos succeeded, navigate to dashboard
            updateShouldShowLeaveWarning(false);
            Navigation.goToLocation(Urls.DASHBOARD);
        }
    },
);

const handlePublishFailureOperation = createLuminaryOperation(
    async ({ dispatch, getState, call }, failedPublishResponse: BulkPublishResponseItem) => {
        switch (failedPublishResponse.error_code) {
            case ErrorEnum.ZERO_SLIDES:
                Toast.error('Cannot publish video without scenes!');
                break;
            case ErrorEnum.VIDEO_DURATION_TOO_LONG:
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: LOCKED_FEATURES.MAX_VIDEO_DURATION_LIMIT,
                        },
                    }),
                );
                break;
            case ErrorEnum.UPGRADE_FOR_AI_AVATAR:
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: LOCKED_FEATURES.CANT_PUBLISH_AI_AVATAR,
                        },
                    }),
                );
                break;
            case ErrorEnum.TRANSITIONED_MEDIA_PRESENT: {
                const mediaErrors = failedPublishResponse.details?.transitioned_asset_errors;
                if (!mediaErrors) {
                    throw new Error('Media errors are undefined');
                }
                analytics.track('User encountered archived assets', {
                    user_id: SERVER.USER.id,
                    email: SERVER.USER.email,
                    error_details: mediaErrors,
                });
                Toast.removeAll();
                dispatch(
                    setMediaErrors({
                        errors: mediaErrors.assets,
                    }),
                );
                call(showMediaErrorOperation);
                break;
            }
            case ErrorEnum.UPGRADE_FOR_UNLIMITED_MEDIA:
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.PREMIUM_MEDIA_UPGRADE,
                    }),
                );
                break;
            case ErrorEnum.HIT_MEDIA_CREDIT_LIMIT: {
                const pricingPlanName = (SERVER.USER.plan || '').toLowerCase();
                const modalToShow = pricingPlanName.includes(PLAN_PREFIX.PREMIUM)
                    ? LOCKED_FEATURES.MARKETPLACE_MEDIA
                    : LOCKED_FEATURES.HIT_MEDIA_CREDIT_LIMIT;

                const creditsAvailable =
                    failedPublishResponse.details?.premium_media_errors?.credits_available;
                const creditsUsed =
                    failedPublishResponse.details?.premium_media_errors?.credits_used;
                const additionalCreditsRequired =
                    failedPublishResponse.details?.premium_media_errors
                        ?.additional_credits_required;

                let premiumMediaCreditsLeft = -1;
                if (
                    typeof creditsAvailable === 'number' &&
                    typeof creditsUsed === 'number' &&
                    typeof additionalCreditsRequired === 'number'
                ) {
                    premiumMediaCreditsLeft =
                        creditsAvailable - creditsUsed - additionalCreditsRequired;
                }
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: modalToShow,
                            premiumMediaCreditsLeft,
                        },
                    }),
                );
                break;
            }
            case ErrorEnum.ALREADY_PUBLISHED:
                await waitForMillis(5000);
                updateShouldShowLeaveWarning(false);
                Navigation.goToLocation(Urls.DASHBOARD + '?save_failure=1');
                break;
            case ErrorEnum.PREMIUM_MEDIA_CREDITS:
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.PREMIUM_MEDIA_UPGRADE,
                    }),
                );
                break;
            case ErrorEnum.HIT_AI_VO_MINUTES_LIMIT:
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: LOCKED_FEATURES.AI_VOICEOVER_MINUTES_LIMIT,
                        },
                    }),
                );
                dispatch(loadUsageLimits(failedPublishResponse.video_id));
                break;
            case ErrorEnum.UNKNOWN_STATE:
                updateShouldShowLeaveWarning(false);
                window.alert(UNKNOWN_SAVE_ERROR_MESSAGE);
                break;
            default:
                Toast.error('An error occurred while publishing the video.');
                break;
        }
    },
);

export function updateShouldShowLeaveWarning(should_show: boolean) {
    /* show a leave confirmation if in the later stages */
    if (should_show && !SERVER.DEBUG) {
        window.onbeforeunload = function (event) {
            event.preventDefault();
        };
    } else if (should_show) {
        window.onbeforeunload = () =>
            Toast.info('Disabling window.onbeforeunload warning in DEBUG mode', 'FYI!');
    } else {
        window.onbeforeunload = null;
    }
}

/**
 * TODO refactor this action to use videoId instead of video object.
 */
export const hydrateVideoCreatorReduxStoresAction = (video: Video) => {
    return (dispatch: AppDispatch, getState: GetRootState): Video => {
        if (video.video_init_type) {
            dispatch(setVideoInitType(video.video_init_type as VIDEO_INIT_TYPE));
        }
        if (video.script_composer_data) {
            dispatch(hydrateScriptComposerData(video.script_composer_data as ScriptComposerData));
        }

        if (video.async_generation_id) {
            dispatch(setAsyncGeneration(video.async_generation_id, video.async_generation_data));
        }

        // setup time tracking
        dispatch(initializeTabDurations(getTabDurationsFromVideo(video)));

        const articleUrl = isPlaceholderArticleUrl(video.article.url) ? '' : video.article.url;
        if (video.article.original_content) {
            dispatch(
                hydrateOriginalContent(
                    articleUrl,
                    video.article.original_content as SerializedEditorState,
                ),
            );
        }
        if (video.article.language) {
            dispatch(
                storeArticleLanguageCode({
                    articleLanguageCode: video.article.language,
                    systemTriggered: true,
                    parentActionGroup: undefined,
                }),
            );
        }
        if (video.document.language) {
            dispatch(
                storeDocumentLanguageCode({
                    documentLanguageCode: video.document.language,
                    systemTriggered: true,
                    parentActionGroup: undefined,
                }),
            );
        }

        dispatch(
            hydrateOverrides({
                overrides: video.user_overrides as UserOverrides,
                systemTriggered: true,
                parentActionGroup: undefined,
            }),
        );
        dispatch(
            hydrateTranscripts(
                video.transcripts as Record<
                    string,
                    SerializedTranscript | SerializedEditorState<SerializedLexicalNode>
                >,
            ),
        );

        dispatch(
            storeArticleUrl({
                articleUrl,
                systemTriggered: true,
                parentActionGroup: undefined,
            }),
        );
        dispatch(
            storeArticleId({
                articleId: video.article.id,
                systemTriggered: true,
                parentActionGroup: undefined,
            }),
        );

        dispatch(
            storeDocumentUrl({
                documentUrl: video.document.path,
                systemTriggered: true,
                parentActionGroup: undefined,
            }),
        );
        dispatch(
            storeDocumentName({
                documentName: video.document.name,
                systemTriggered: true,
                parentActionGroup: undefined,
            }),
        );
        dispatch(
            storeDocumentId({
                documentId: video.document.id,
                systemTriggered: true,
                parentActionGroup: undefined,
            }),
        );

        // store data
        dispatch(setVideoSlug({ videoSlug: video.slug ?? null }));
        dispatch(
            setVideoCreationSource({
                creationSource: video.creation_source ?? null,
            }),
        );
        dispatch(
            setIsAnimationVideo({
                isAnimationVideo: Boolean(video.animation_data),
            }),
        );
        dispatch(
            setParentVideoLanguage({
                language: video.edit_parent_language ?? null,
            }),
        );
        dispatch(
            setVideoDataHash({
                videoDataHash: video.video_data_hash,
            }),
        );
        dispatch(setSharePageUrl({ sharePageUrl: video.page_url }));
        dispatch(
            setVideoVisibility({
                videoVisibility: video.visibility,
            }),
        );
        dispatch(setVideoNameOperation(video.name));
        dispatch(
            setSelectedTemplateId({
                templateId: video.template_id,
            }),
        );
        dispatch(
            setVideoLanguage({
                video_language: video.language as LanguageEnum,
            }),
        );
        dispatch(setVideoProject({ projectId: video.project }));
        dispatch(setVideoOwnerId({ videoOwnerId: video.user_id }));
        dispatch(
            setBrandKitId({
                brandKitId: video.brand_kit_id,
                systemTriggered: false,
                parentActionGroup: undefined,
            }),
        );
        if (video.blueprint_id) {
            dispatch(
                setBlueprintId({
                    blueprintId: video.blueprint_id,
                }),
            );
        }
        const videoId = video.id;
        dispatch(setVideoIdAndHookupAnalytics(videoId));

        // TODO reset undo-redo state

        return video;
    };
};

type OpenRelevantSceneSelectorParams = {
    sceneId: string;
    sceneGroupId: string;
    sceneGroupCategory: string;
    isOverlay: boolean;
};

/**
 * Opens the appropriate scene selector (fullscreen or swap) based on whether the user has modified
 * the scene content. If the scene content matches the default for its scene design, opens the
 * fullscreen selector. Otherwise, opens the swap scene selector.
 */
export const openRelevantSceneSelectorOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        { sceneId, sceneGroupId, sceneGroupCategory, isOverlay }: OpenRelevantSceneSelectorParams,
    ) => {
        // Get the current scene from luminary
        const currentScene = luminary.getSceneById(sceneId);
        if (!currentScene) {
            throw new Error(`Scene with id ${sceneId} not found`);
        }

        const templateId = luminary.template.id;
        const sceneDesign = currentScene.sceneDesign;

        const currentSceneGroup = currentScene.sceneGroup;
        if (!currentSceneGroup) {
            throw new Error(`Scene group not found for scene with id ${sceneId}`);
        }
        const canUseFullscreenSelectorForSceneGroupCategory =
            isOrdinarySceneGroup(currentSceneGroup) || isOutroSceneGroup(currentSceneGroup);
        let hasUserModifications = true;
        if (canUseFullscreenSelectorForSceneGroupCategory) {
            // Create a fresh scene with the same design to compare against
            const templateBundle = getTemplateBundle(getState(), templateId);
            hasUserModifications = withDataLuminarySync(
                (dataLuminary) => {
                    dataLuminary.aspectRatio = luminary.aspectRatio;
                    const { scene } = dataLuminary.addSceneGroup({ sceneDesignId: sceneDesign });
                    return checkSceneHasUserModifications(currentScene, scene);
                },
                { templateBundle: templateBundle ?? undefined },
            );
        }

        if (!canUseFullscreenSelectorForSceneGroupCategory || hasUserModifications) {
            analytics.track('Scene design selection triggered by user', {
                isOverlay,
                sceneId,
                sceneGroupId,
            });
            dispatch(
                setSwapSceneDesignForSlide({
                    sceneId,
                }),
            );
            dispatch(showFullscreenSwapSceneSelector());
        } else {
            // Scene content matches default, use fullscreen selector
            dispatch(
                setSceneSelectorForSlide({
                    sceneId,
                }),
            );
            dispatch(
                showFullscreenSelector({
                    targetSlideIndex: null,
                    targetSlideId: sceneId,
                    targetSlideGroupId: sceneGroupId,
                    sceneGroupCategory,
                }),
            );
        }
    },
);

function checkSceneHasUserModifications(currentScene: Scene, defaultScene: Scene): boolean {
    // Get all editable assets from both scenes
    const currentAssets = currentScene.getAssets().filter((asset) => asset.isEditable);
    const defaultAssets = defaultScene.getAssets().filter((asset) => asset.isEditable);

    // Create a map of default assets by name for easier lookup
    const defaultAssetsByName = new Map<string, (typeof defaultAssets)[0]>();
    defaultAssets.forEach((asset) => {
        defaultAssetsByName.set(asset.name, asset);
    });

    // Compare each current asset with its default counterpart
    for (const currentAsset of currentAssets) {
        const defaultAsset = defaultAssetsByName.get(currentAsset.name);
        if (!defaultAsset) {
            return true;
        }

        // Compare text assets
        if (VideoData.isTextAsset(currentAsset) && VideoData.isTextAsset(defaultAsset)) {
            if (currentAsset.text !== defaultAsset.text) {
                return true;
            }
        }

        // Compare media assets
        if (VideoData.isMediaAsset(currentAsset) && VideoData.isMediaAsset(defaultAsset)) {
            if (!areSourceDataEqual(currentAsset.sourceData, defaultAsset.sourceData)) {
                return true;
            }
        }

        // Compare icon assets
        if (VideoData.isIconAsset(currentAsset) && VideoData.isIconAsset(defaultAsset)) {
            if (!areSourceDataEqual(currentAsset.sourceData, defaultAsset.sourceData)) {
                return true;
            }
        }
    }

    return false;
}

function areSourceDataEqual(
    sourceData1: MediaSourceData | IconSourceData,
    sourceData2: MediaSourceData | IconSourceData,
): boolean {
    return (
        sourceData1.source === sourceData2.source && sourceData1.source_id === sourceData2.source_id
    );
}
